import React, { useEffect } from "react";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DonateButtonVariant } from "../Button/Button";
import { getImageSrc } from "../../../utils/imageMapping";
import { Images } from "../../../utils/Images";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";
import { validateDonationAmount } from "../../../utils/donationValidation";

const CampaignHeader = ({ campaignData, donationAmount = 1000 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  // Doghome-style breakpoints for banner image
  const is1920 = useMediaQuery("(min-width:1920px)");
  const is1600 = useMediaQuery("(min-width:1600px) and (max-width:1919px)");
  const is1440 = useMediaQuery("(min-width:1440px) and (max-width:1599px)");
  const is1280 = useMediaQuery("(min-width:1280px) and (max-width:1439px)");
  const is1024 = useMediaQuery("(min-width:1024px) and (max-width:1279px)");
  const is768 = useMediaQuery("(min-width:768px) and (max-width:1023px)");
  const is568 = useMediaQuery("(min-width:568px) and (max-width:767px)");
  const is475 = useMediaQuery("(min-width:475px) and (max-width:567px)");
  const is320 = useMediaQuery("(min-width:320px) and (max-width:474px)");

  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const navigate = useNavigate();

  useEffect(() => {
    if (!cmsHome) dispatch(fetchCmsHomeAction());
  }, [cmsHome, dispatch]);

  const handleDonateClick = () => {
    const amount = donationAmount ?? 1000;
    const validation = validateDonationAmount(amount, cmsHome, false, "₹");
    if (!validation.isValid) {
      alert(validation.error);
      return;
    }
    navigate("/donate-once-page", {
      state: {
        campaignTitle: campaignData?.title || "",
        campaignId: campaignData?.id || null,
        amount,
      },
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        mb: isMobile ? 1 : 6,
      }}
    >
      {/* Header Section with Title and Donate Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center", // Changed from flex-start to center
          mb: isMobile ? "20px" : "32px",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
        }}
      >
        {/* Title Section */}
        <Box
          sx={{
            flex: 1,
            mr: isMobile ? 0 : 10,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              // fontFamily: "Red Hat Display",
              fontWeight: 700,
              fontStyle: "Bold",
              fontSize: isMobile ? "18px" : isTablet ? "32px" : "46px", // Updated mobile font size
              lineHeight: isMobile ? "30px" : "150%", // Updated mobile line height
              letterSpacing: "-1%",
              verticalAlign: "middle",
              textTransform: "capitalize",
              color: "#1D1D1B",
              width: "100%", // Changed from fixed width to 100%
              maxWidth: "100%",
              // Custom media query for screens 1200px and below
              "@media (max-width: 1200px)": {
                fontSize: isMobile ? "18px" : isTablet ? "32px" : "32px", // Updated mobile font size
                lineHeight: isMobile ? "30px" : "140%", // Updated mobile line height
              },
              // Custom media query for screens 870px and below
              "@media (max-width: 870px)": {
                fontSize: isMobile ? "18px" : "20px", // Updated mobile font size
                lineHeight: isMobile ? "30px" : "135%", // Updated mobile line height
              },
            }}
          >
            {campaignData?.title}
          </Typography>
        </Box>

        {/* Donate Button Section - Hidden on Mobile */}
        {!isMobile && (
          <Box
            sx={{
              flexShrink: 0,
              alignSelf: "center", // Changed from flex-start to center
            }}
          >
            <DonateButtonVariant
              text="Donate Now"
              onClick={handleDonateClick}
              isMobile={isMobile}
            />
          </Box>
        )}
      </Box>

      {/* Campaign Image */}
      <Box
        sx={{
          width: "100%",
          borderRadius: "16px",
          overflow: "hidden",
          mb: isMobile ? 3 : 4,
          position: "relative",
        }}
      >
        <Box
          component="img"
          src={getImageSrc(campaignData?.bannerImage) || Images.fallBackImageBanner}
          alt={campaignData?.title || "Campaign Image"}
          sx={{
            width: is768 || is568 || is475 || is320
              ? is475 || is320
                ? "100%"
                : "100%"
              : "100%", // Always 100% on desktop to fit container
            maxWidth: is1920
              ? "100%"
              : is1600
              ? "100%"
              : is1440
              ? "100%"
              : is1280
              ? "100%"
              : is1024
              ? "100%"
              : "100%", // Max width constraints for large screens
            // Use aspect ratio instead of fixed height for better consistency
            aspectRatio: "2.7/1",
            borderRadius: "16px",
            margin: "0px auto",
            objectFit: "cover",
            display: "block",
            boxSizing: "border-box",
            // Fallback height for browsers that don't support aspect-ratio
            height: is768 || is568 || is475 || is320 ? "300px" : "442px",
            "@supports (aspect-ratio: 2.7/1)": {
              height: "auto",
            },
            // Max height to prevent long images from becoming too tall
            maxHeight: is768 || is568 || is475 || is320 ? "250px" : "442px",
            // Additional styling for better visual appeal
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
            // Ensure image loads smoothly
            backgroundColor: "#f5f5f5", // Light background while loading
          }}
        />
        {/* Tax Benefit Badge - Top Left */}
        {campaignData?.taxBenefit && (
          <Chip
            label={campaignData.taxBenefit}
            sx={{
              position: "absolute",
              left: isMobile ? "8px" : "10px",
              top: isMobile ? "8px" : "10.5px",
              background: "#A9C74E",
              color: "#FFFFFF",
              fontFamily: "Red Hat Display",
              fontWeight: "700",
              fontSize: isMobile ? "10px" : isTablet ? "14px" : "16px",
              height: isMobile ? "28px" : isTablet ? "36px" : "40px",
              borderRadius: "4px",
              "& .MuiChip-label": {
                padding: isMobile ? "0 12px" : isTablet ? "0 18px" : "0 20px",
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default CampaignHeader;
